<template>
  <div class="flex gap-1 items-center">
    <WatchlistIcon />

    <NButton
      v-gid="{
        module: 'order',
        screen: 'home',
        section: 'symbolDetailSearch',
        component: 'buyButton',
      }"
      size="mini"
      variant="fill"
      @click.stop="handleBuy"
    >
      <NText variant="overline" weight="medium">
        {{ t('common.buy') }}
      </NText>
    </NButton>

    <NButton
      v-gid="{
        module: 'order',
        screen: 'home',
        section: 'symbolDetailSearch',
        component: 'sellButton',
      }"
      size="mini"
      color="red"
      variant="fill"
      :disabled="offersIsin.includes(activeIsin)"
      @click.stop="openNewOrderFormDialog({ type: ORDER_TYPES.sell, symbol })"
    >
      <NText variant="overline" weight="medium">
        {{ t('common.sell') }}
      </NText>
    </NButton>

    <NPopper hover offset-y="8">
      <NegarMarketDepthIcon
        v-gid="{
          module: 'symbol',
          screen: 'home',
          section: 'symbolDetailSearchMarketDepth',
          component: isShowMarketDepth
            ? 'closeMarketDepthButton'
            : 'openMarketDepthButton',
        }"
        size="20"
        class="cursor-pointer"
        @click.stop="emit('handleMarketDepth')"
      />
      <template #content>
        <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
          <NText
            variant="caption2"
            weight="medium"
            class="text-neutral-quaternary"
          >
            {{ t('symbol.marketDepth') }}
          </NText>
        </div>
      </template>
    </NPopper>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { NegarMarketDepthIcon } from '@negar/icons';
import { useLiveInitialPublicOffers } from '@core/ipo';
import { ORDER_TYPES } from '@core/order';

import { WatchlistIcon } from '@/modules/watchlist';
import { useOrderDialogs } from '@/modules/order';
import { useActiveSymbolIsin, useLiveSymbolQuery } from '@/modules/symbol';
import { useIpoDialogs } from '@/modules/ipo';

type SymbolActionsEmits = {
  (e: 'handleMarketDepth'): void;
};

type SymbolActionsProps = {
  isShowMarketDepth?: boolean;
};

const emit = defineEmits<SymbolActionsEmits>();

defineProps<SymbolActionsProps>();

const { t } = useI18n();

const { offersIsin, initialPublicOffers } = useLiveInitialPublicOffers();

const { openNewOrderFormDialog } = useOrderDialogs();
const { openBuyOfferDialog } = useIpoDialogs();

const { activeIsin } = useActiveSymbolIsin();
const { symbol } = useLiveSymbolQuery(activeIsin);

const handleBuy = () => {
  const offer = initialPublicOffers.value?.find(
    (offer) => offer.instrumentIsin === activeIsin.value
  );

  if (offer && !offer.isUnderwriting) {
    openBuyOfferDialog({ offer });
  } else {
    openNewOrderFormDialog({
      type: ORDER_TYPES.buy,
      symbol: symbol.value,
      isUnderWriting: offer?.isUnderwriting,
    });
  }
};
</script>
